import { Group, SegmentedControl, Select } from "@mantine/core";
import { IconChevronDown, IconChevronRight } from "@tabler/icons-react";
import * as _ from "lodash-es";
import {
  MantineReactTable,
  type MRT_ColumnDef,
  MRT_ExpandButton,
  type MRT_ExpandedState,
  type MRT_TableOptions,
  useMantineReactTable
} from "mantine-react-table-open";
import { MRT_Localization_DE } from "mantine-react-table-open/locales/de/index.esm.mjs";
import { useMemo, useState } from "react";
import { LoadOrError } from "../../../components/LoadOrError/LoadOrError";
import { useShouldShowStaffView } from "../../../components/StaffViewToggle/useShouldShowStaffView";
import { getDefaultMRTOptions } from "../../../mantine/getDefaultMRTOptions";
import { emptyArray } from "../../../utils/constants";
import { getErrorText } from "../../../utils/get-error-text";
import { useActiveUsers } from "../../UserManagement/hooks/useActiveUsers";
import type { ActiveUser } from "../../UserManagement/UserManagement.types";
import type { ExpressOnboardingGenerator } from "../ExpressOnboarding.types";
import { getExpressOnboardingTableAZWColumn } from "./columns/getExpressOnboardingTableAZWColumn";
import { getExpressOnboardingTableEegInbetriebnahmeColumn } from "./columns/getExpressOnboardingTableEegInbetriebnahmeColumn";
import { getExpressOnboardingTableFoerderregimeColumn } from "./columns/getExpressOnboardingTableFoerderregimeColumn";
import { getExpressOnboardingTableGebotsterminColumn } from "./columns/getExpressOnboardingTableGebotsterminColumn";
import { getExpressOnboardingTableGenerationShareColumn } from "./columns/getExpressOnboardingTableGenerationShareColumn";
import { getExpressOnboardingTableJahrDesGebotsterminsColumn } from "./columns/getExpressOnboardingTableJahrDesGebotsterminsColumn";
import { getExpressOnboardingTableLocationIdColumn } from "./columns/getExpressOnboardingTableLocationIdColumn";
import { getExpressOnboardingTableSrIdColumn } from "./columns/getExpressOnboardingTableSrIdColumn";
import { getExpressOnboardingTableTrIdColumn } from "./columns/getExpressOnboardingTableTrIdColumn";
import { getExpressOnboardingTableVerausserungsformColumn } from "./columns/getExpressOnboardingTableVerausserungsformColumn";
import { ExpressOnboardingTableSiteCell } from "./ExpressOnboardingTableSiteCell";
import { useExpressOnboardingEditingState } from "./hooks/useExpressOnboardingEditingState";
import useExpressOnboardingGenerators from "./hooks/useExpressOnboardingGenerators";
import { useExpressOnboardingTodoStatus } from "./hooks/useExpressOnboardingTodoStatus";
import { getDefaultExpandedSites } from "./utils/getDefaultExpandedSites";
import { getIsConfigurationTodoOpen } from "./utils/getIsConfigurationTodoOpen";

const ControlColumns = {
  allgemeineDaten: "Allgemeine Daten",
  eegAnlage: "Daten zur EEG-Anlage"
} as const;

const siteColumn: MRT_ColumnDef<ExpressOnboardingGenerator> = {
  id: "site_id",
  accessorKey: "site_id",
  header: "Liegenschaft"
};
const colsForAll: Array<MRT_ColumnDef<ExpressOnboardingGenerator>> = [
  {
    id: "name",
    accessorKey: "name",
    header: "Name",
    Cell: ({ renderedCellValue }) => {
      return <span style={{ marginLeft: 32 }}>{renderedCellValue}</span>;
    }
  },
  { id: "mastr_nr", accessorKey: "mastr_nr", header: "MaStR-Nr" }
];

interface ExpressOnboardingGeneratorsTableProps {
  siteId: number;
}

function ExpressOnboardingGeneratorsTable({
  siteId
}: ExpressOnboardingGeneratorsTableProps) {
  const [control, setControl] =
    useState<keyof typeof ControlColumns>("allgemeineDaten");
  const [selectedUser, setSelectedUser] = useState<ActiveUser["id"] | null>(
    null
  );
  const [expanded, setExpanded] = useState<MRT_ExpandedState>();
  const editing = useExpressOnboardingEditingState();
  const { getError, setError } = editing;

  const { shouldShowStaffView } = useShouldShowStaffView();

  const {
    data: activeUsers,
    isLoading: isActiveUsersLoading,
    error: activeUsersError
  } = useActiveUsers(shouldShowStaffView);
  const {
    data: generators,
    isLoading,
    error
  } = useExpressOnboardingGenerators(selectedUser);

  /* the site the modal was opened from is always shown first */
  const sortedGenerators = useMemo(
    () =>
      generators && [
        ...generators.filter((generator) => generator.site_id === siteId),
        ...generators.filter((generator) => generator.site_id !== siteId)
      ],
    [generators, siteId]
  );

  const { data: sitesTodos, isLoading: isSiteTodosLoading } =
    useExpressOnboardingTodoStatus(selectedUser);

  const sitesTodosMap = useMemo(
    () =>
      sitesTodos?.reduce(
        (map, siteTodo) => {
          map[siteTodo.site_id] = siteTodo;
          return map;
        },
        {} as Record<number, (typeof sitesTodos)[number]>
      ),
    [sitesTodos]
  );

  /* sites with an open todo stay collapsed until the todo is done */
  const getIsSiteLocked = (generatorSiteId: number) =>
    getIsConfigurationTodoOpen(sitesTodosMap?.[generatorSiteId]);

  /* the default is only set once the data is loaded,
     so a site does not collapse while the user fills in its last missing value */
  if (expanded === undefined && generators) {
    setExpanded(getDefaultExpandedSites(generators));
  }
  const currentExpanded = expanded ?? {};

  const basicCols: Array<MRT_ColumnDef<ExpressOnboardingGenerator>> = [
    getExpressOnboardingTableSrIdColumn(getError, setError),
    getExpressOnboardingTableTrIdColumn(getError, setError),
    getExpressOnboardingTableLocationIdColumn(getError, setError),
    getExpressOnboardingTableGenerationShareColumn(getError, setError)
  ];
  const eegAnlageCols: Array<MRT_ColumnDef<ExpressOnboardingGenerator>> = [
    getExpressOnboardingTableEegInbetriebnahmeColumn(getError, setError),
    getExpressOnboardingTableFoerderregimeColumn(editing),
    getExpressOnboardingTableJahrDesGebotsterminsColumn(editing),
    getExpressOnboardingTableGebotsterminColumn(editing),
    getExpressOnboardingTableAZWColumn(getError, setError),
    getExpressOnboardingTableVerausserungsformColumn(editing)
  ];

  const defaultOptions = getDefaultMRTOptions<ExpressOnboardingGenerator>({
    fitModalHeight: true,
    hideBorder: true,
    searchTextInput: {
      placeholder: "Suche nach Liegenschaft oder Erzeuger..."
    },
    initialState: {
      showGlobalFilter: true,
      grouping: ["site_id"],
      columnPinning: { left: ["name", "mastr_nr"] }
    }
  });
  const customOptions: Partial<MRT_TableOptions<ExpressOnboardingGenerator>> = {
    enableColumnPinning: true,
    enableGrouping: true,
    groupedColumnMode: "remove",
    paginateExpandedRows: false,
    localization: {
      ...MRT_Localization_DE,
      rowsPerPage: "Liegenschaften pro Seite"
    },
    state: { expanded: currentExpanded },
    onExpandedChange: (updater) =>
      setExpanded((prev) =>
        typeof updater === "function" ? updater(prev ?? {}) : updater
      ),
    getRowCanExpand: (row) =>
      row.getIsGrouped() && !getIsSiteLocked(row.original.site_id),
    /* the expanded row model ignores `getRowCanExpand`, so locked sites have to be collapsed here */
    getIsRowExpanded: (row) =>
      row.getCanExpand() &&
      (currentExpanded === true || !!currentExpanded[row.id]),
    displayColumnDefOptions: {
      "mrt-row-expand": {
        Cell: ({ row, table }) => {
          if (row.groupingColumnId) {
            const siteTodos = sitesTodosMap?.[row.original.site_id];
            return (
              <ExpressOnboardingTableSiteCell
                backendData={generators}
                row={row}
                siteTodosStatus={siteTodos}
                table={table}
              />
            );
          }
          return <MRT_ExpandButton row={row} table={table} />;
        }
      }
    },
    positionToolbarAlertBanner: "none",
    enableColumnDragging: false,
    enableSorting: false,
    globalFilterFn: "filterForSitesAndGenerators",
    filterFns: {
      filterForSitesAndGenerators: (row, _, filterValue) => {
        const lowerCaseFilterValue = filterValue.toLowerCase();

        return (
          row.original.site_name.toLowerCase().includes(lowerCaseFilterValue) ||
          row.original.site_id.toString().includes(lowerCaseFilterValue) ||
          row
            .getValue<ExpressOnboardingGenerator["name"]>("name")
            .toLowerCase()
            .includes(lowerCaseFilterValue)
        );
      }
    },
    enableExpandAll: false,
    mantineExpandButtonProps: ({ row }) => ({
      size: row.groupingColumnId !== "site_id" ? 0 : "sm",
      children: row.getIsExpanded() ? <IconChevronDown /> : <IconChevronRight />
    }),
    renderTopToolbarCustomActions: () => {
      return (
        <Group gap="xs" justify="flex-start" ms="xs" w="100%">
          <SegmentedControl
            data={Object.entries(ControlColumns).map(([value, label]) => ({
              value,
              label
            }))}
            value={control}
            onChange={(newValue) =>
              setControl(newValue as keyof typeof ControlColumns)
            }
          />
        </Group>
      );
    },
    /* hide grouping cell */
    mantineTableHeadCellProps: ({ column }) => ({
      display: column.getIndex() === 0 ? "none" : undefined
    }),
    /* hide grouping cells and useless chevrons */
    mantineTableBodyCellProps: ({ column, row, table }) => ({
      colSpan:
        row.getIsGrouped() && column.getIndex() === 0
          ? table.getVisibleLeafColumns().length - 1
          : undefined,
      onClick: row.getToggleExpandedHandler(),
      display:
        (row.getIsGrouped() && column.getIndex() !== 0) ||
        (!row.getIsGrouped() && column.getIndex() === 0)
          ? "none"
          : undefined
    })
  };

  const groupingColumn = [siteColumn];
  const allgemeineDatenColumns = [
    ...groupingColumn,
    ...colsForAll,
    ...basicCols
  ];
  const eegAnlageColumns = [...groupingColumn, ...colsForAll, ...eegAnlageCols];

  const mrtTable = useMantineReactTable<ExpressOnboardingGenerator>({
    ..._.merge({}, defaultOptions, customOptions),
    columns:
      control === "allgemeineDaten" ? allgemeineDatenColumns : eegAnlageColumns,
    data: sortedGenerators ?? emptyArray
  });

  const userOptions = useMemo(
    () =>
      activeUsers
        ? activeUsers
            .map((user) => ({
              value: user.id,
              label: `${user.name} (${user.email})`
            }))
            .sort((a, b) => a.label.localeCompare(b.label))
        : [],
    [activeUsers]
  );

  return (
    <>
      {shouldShowStaffView && (
        <Group justify="flex-end">
          <Select
            clearable
            data={userOptions}
            error={
              activeUsersError ? getErrorText(activeUsersError) : undefined
            }
            loading={isActiveUsersLoading}
            placeholder="Einzelnen Nutzer auswählen"
            value={selectedUser}
            w={400}
            onChange={(userId) => {
              setSelectedUser(userId);
              setExpanded(undefined);
            }}
          />
        </Group>
      )}
      <LoadOrError error={error} loading={isLoading || isSiteTodosLoading}>
        <MantineReactTable table={mrtTable} />
      </LoadOrError>
    </>
  );
}

export { ExpressOnboardingGeneratorsTable };
